import React, { useEffect, useMemo, useState } from "react";
import { appRepo } from "../../repo/app-repo";
import { AppLinks } from "../../entry/server/app";
import appleIcon from "@public/img/app-apple.svg";
import bazaarIcon from "@public/img/app-bazaar.png";
import downloadIcon from "@public/img/app-download.svg";
import "@/styles/app-links-section.css";

type PlatformKey = keyof AppLinks;

type PlatformConfig = {
  key: PlatformKey;
  label: string;
  variant: "icon" | "text";
  icon: string;
  iconWidth: number;
  iconHeight: number;
};

const PLATFORMS: PlatformConfig[] = [
  { key: "android", label: "اپ اندروید", variant: "text", icon: downloadIcon, iconWidth: 32, iconHeight: 32 },
  { key: "ios", label: "اپ iOS", variant: "icon", icon: appleIcon, iconWidth: 20, iconHeight: 28 },
  { key: "android_tv", label: "اپ اندروید تی‌وی", variant: "text", icon: downloadIcon, iconWidth: 32, iconHeight: 32 },
  { key: "windows", label: "ویندوز", variant: "text", icon: downloadIcon, iconWidth: 32, iconHeight: 32 },
  { key: "macos", label: "مک", variant: "text", icon: downloadIcon, iconWidth: 32, iconHeight: 32 },
  { key: "linux", label: "لینوکس", variant: "text", icon: downloadIcon, iconWidth: 32, iconHeight: 32 },
];

type PlatformButton = PlatformConfig & { href: string };

function toButton(platform: PlatformConfig, href: string): PlatformButton {
  if (platform.key === "android" && href.indexOf("cafebazaar") !== -1) {
    return {
      key: platform.key,
      label: "کافه بازار",
      variant: "icon",
      icon: bazaarIcon,
      iconWidth: 26,
      iconHeight: 28,
      href: href,
    };
  }

  return {
    key: platform.key,
    label: platform.label,
    variant: platform.variant,
    icon: platform.icon,
    iconWidth: platform.iconWidth,
    iconHeight: platform.iconHeight,
    href: href,
  };
}

export default function AppLinksSection(): React.ReactElement | null {
  const [links, setLinks] = useState<AppLinks | null>(null);

  useEffect(function () {
    var cancelled = false;

    appRepo.getAppLinks().then(function (res) {
      if (cancelled || !res.status || !res.data) {
        return;
      }
      setLinks(res.data);
    });

    return function () {
      cancelled = true;
    };
  }, []);

  const available = useMemo(function () {
    if (!links) {
      return [] as PlatformButton[];
    }

    var result: PlatformButton[] = [];
    for (var i = 0; i < PLATFORMS.length; i = i + 1) {
      var platform = PLATFORMS[i];
      var raw = links[platform.key];
      var href = raw ? raw.trim() : "";
      if (href) {
        result.push(toButton(platform, href));
      }
    }
    return result;
  }, [links]);

  if (available.length === 0) {
    return null;
  }

  return (
    <section className="app-links">
      <div className="app-links__card">
        <div className="app-links__intro">
          <p className="app-links__title">
            دانلود اپلیکیشن <span className="app-links__brand">WONMOV</span>
          </p>
          <p className="app-links__subtitle">سریع‌ترین راه تماشای فیلم و سریال</p>
        </div>

        <div className="app-links__buttons">
          {available.map(function (platform) {
            if (platform.variant === "icon") {
              return (
                <a
                  key={platform.key}
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={"دانلود " + platform.label}
                  className="app-links__btn app-links__btn--icon"
                >
                  <span className="app-links__btn-icon-wrap">
                    <img
                      src={platform.icon}
                      alt=""
                      width={platform.iconWidth}
                      height={platform.iconHeight}
                    />
                  </span>
                </a>
              );
            }

            return (
              <a
                key={platform.key}
                href={platform.href}
                target="_blank"
                rel="noopener noreferrer"
                className="app-links__btn app-links__btn--text"
              >
                <img
                  className="app-links__btn-icon"
                  src={platform.icon}
                  alt=""
                  width={platform.iconWidth}
                  height={platform.iconHeight}
                />
                <span className="app-links__btn-label">
                  <span className="app-links__btn-label-top">دانلود</span>
                  <span className="app-links__btn-label-main">{platform.label}</span>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
